import type { ILocatedPropsAsObject } from '@vendors/contentful/cms';
import type { ISeo } from '@vendors/contentful/cms/renderers/Seo/types';
import type CataloguesType from '@migrated/context/types/catalogues';
import { Metadata } from '@src/migrated-bundle-components/utils/types';
import { EURStablecoinBasket, USDStablecoin, USDStablecoinBasket } from './coins';

export type CoinMetadata = {
  code: string;
  metadata: Metadata;
  key: string;
};

export const parseCoinsMetadata = (currencies: CataloguesType['currencies']) => {
  const supportedCurrenciesMetadata: Array<Metadata> = [];

  currencies?.supported.forEach(currency => {
    const supportedCurrencyMetadata = currencies?.metadata.find(c => c.code === currency);

    if (supportedCurrencyMetadata?.type !== 'crypto') return;

    const includesStableCoins = [USDStablecoin, ...USDStablecoinBasket, ...EURStablecoinBasket]
      .filter(coin => coin !== 'usdc')
      .includes(supportedCurrencyMetadata.code);

    if (includesStableCoins) return;

    supportedCurrenciesMetadata.push(supportedCurrencyMetadata);
  });

  const coinsMetadata = supportedCurrenciesMetadata.reduce(
    (acc, metadata) => {
      return Object.entries(acc).reduce((accNested, [minor]) => {
        if (!accNested[minor]) {
          return accNested;
        }
        return {
          ...accNested,
          [minor]: [
            ...accNested[minor],
            {
              metadata,
              code: metadata.code,
              key: metadata.code,
            },
          ],
        };
      }, acc);
    },
    { usd: [], mxn: [], ars: [], brl: [], cop: [] } as Record<string, Array<CoinMetadata>>,
  );

  const coinsMetadataOrderByMinor = Object.fromEntries(
    Object.entries(coinsMetadata).map(([minor, coins]) => {
      const coinsOrderByMinorAndPrice = coins.slice();
      return [minor, coinsOrderByMinorAndPrice];
    }),
  );
  return coinsMetadataOrderByMinor;
};

export const parseCoinPageContentfulData = <Content extends Record<string, unknown>>(
  contentfulData?: ILocatedPropsAsObject<Content>,
) => {
  const seoTags = contentfulData?.seoTags as unknown as ISeo | undefined;
  const hint = typeof contentfulData?.content?.hint === 'string' ? contentfulData?.content?.hint : undefined;
  const title = typeof contentfulData?.content?.title === 'string' ? contentfulData?.content?.title : undefined;
  const faq = contentfulData?.content?.faqSection
    ? (contentfulData?.content.faqSection as {
        heading: string;
        faqItems: Array<{
          id: string;
          heading: string;
          paragraph: string;
        }>;
      })
    : undefined;
  const about = contentfulData?.content?.aboutSection
    ? (contentfulData?.content.aboutSection as { title: string; url: string; description: string })
    : undefined;

  return {
    faq,
    about,
    hint,
    title,
    seoTags,
  };
};

export const pricesPageContentfulData = <Content extends Record<string, unknown>>(
  contentfulData?: ILocatedPropsAsObject<Content>,
) => {
  const seoTags = contentfulData?.seoTags as unknown as ISeo | undefined;
  const heading = typeof contentfulData?.content?.heading === 'string' ? contentfulData?.content?.heading : undefined;
  const faq = contentfulData?.content?.faq
    ? (contentfulData?.content.faq as {
        heading: string;
        faqItems: Array<{
          id: string;
          heading: string;
          paragraph: string;
        }>;
      })
    : undefined;

  return {
    faq,
    heading,
    seoTags,
  };
};
